// tab/src/components/AuditDashboard/AuditDashboard.tsx
// OnRounds Phase 2 - Audit Dashboard Component

import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Card,
  Text,
  Button,
  Input,
  Dropdown,
  Option,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import {
  Search24Regular,
  DocumentSearch24Regular,
  ArrowDownload24Regular,
  Filter24Regular,
  ArrowClockwise24Regular,
} from '@fluentui/react-icons';
import { format as formatDate, subDays, startOfDay, endOfDay } from 'date-fns';
import { useTeam } from '../../context/TeamContext';
import { useUser } from '../../context/UserContext';
import { api } from '../../services/api';
import { Loading } from '../common/Loading';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    ...shorthands.gap('16px'),
  },
  filters: {
    display: 'flex',
    alignItems: 'flex-end',
    ...shorthands.gap('12px'),
    flexWrap: 'wrap',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
  },
  filterLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: 'flex',
    ...shorthands.gap('8px'),
  },
  tableCard: {
    ...shorthands.overflow('auto'),
  },
  table: {
    minWidth: '800px',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke1),
  },
  paginationInfo: {
    color: tokens.colorNeutralForeground3,
  },
  paginationButtons: {
    display: 'flex',
    ...shorthands.gap('8px'),
  },
  actionBadge: {
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderRadius('4px'),
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    ...shorthands.gap('16px'),
  },
  summaryCard: {
    ...shorthands.padding('16px'),
  },
  detailsJson: {
    ...shorthands.padding('8px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('4px'),
    fontSize: tokens.fontSizeBase200,
    fontFamily: 'monospace',
    maxWidth: '300px',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

interface AuditEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  userId: string;
  userDisplayName: string;
  timestamp: string;
  details: any;
}

interface AuditAction {
  value: string;
  label: string;
}

interface AuditEntityType {
  value: string;
  label: string;
}

export const AuditDashboard: React.FC = () => {
  const styles = useStyles();
  const { currentTeam } = useTeam();
  const { user } = useUser();

  // Filter state
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [actionFilter, setActionFilter] = useState<string>('');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Data state
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter options
  const [actions, setActions] = useState<AuditAction[]>([]);
  const [entityTypes, setEntityTypes] = useState<AuditEntityType[]>([]);

  // Load filter options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [actionsResult, entityTypesResult] = await Promise.all([
          api.getAuditActions(),
          api.getAuditEntityTypes(),
        ]);
        setActions(actionsResult.actions);
        setEntityTypes(entityTypesResult.entityTypes);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    loadOptions();
  }, []);

  // Search audit log
  const searchAudit = useCallback(async () => {
    if (!currentTeam) return;

    try {
      setIsLoading(true);
      setError(null);

      const result = await api.searchAudit({
        teamId: currentTeam.id,
        startDate: startOfDay(startDate).toISOString(),
        endDate: endOfDay(endDate).toISOString(),
        action: actionFilter || undefined,
        entityType: entityTypeFilter || undefined,
        searchTerm: searchTerm || undefined,
        page,
        pageSize,
      });

      setEntries(result.entries || (result as any).results || []);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      console.error('Failed to search audit log:', err);
      setError(err.message || 'Failed to search audit log');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam, startDate, endDate, actionFilter, entityTypeFilter, searchTerm, page, pageSize]);

  useEffect(() => {
    searchAudit();
  }, [searchAudit]);

  // Export audit log
  const handleExport = async (exportFormat: 'csv' | 'json') => {
    if (!currentTeam) return;
  
    try {
      setIsExporting(true);
  
      const blob = await api.exportAudit(
        currentTeam.id,
        startOfDay(startDate).toISOString(),
        endOfDay(endDate).toISOString(),
        exportFormat
      );
  
      // Download the file
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${exportFormat}-${formatDate(startDate, 'yyyy-MM-dd')}-${formatDate(endDate, 'yyyy-MM-dd')}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to export audit log:', err);
      setError(err.message || 'Failed to export audit log');
    } finally {
      setIsExporting(false);
    }
  };

  const getActionLabel = (action: string) => {
    return actions.find((a) => a.value === action)?.label || action.replace(/_/g, ' ');
  };

  const getEntityTypeLabel = (entityType: string) => {
    return entityTypes.find((e) => e.value === entityType)?.label || entityType;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (!user?.isSupervisor && !user?.isAdmin) {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          You don't have permission to view the audit dashboard.
        </MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <Text size={500} weight="semibold">Audit Dashboard</Text>
          <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
            Search and export audit logs for compliance
          </Text>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className={styles.filters}>
        <div className={styles.filterGroup}>
  <Text className={styles.filterLabel}>Start Date</Text>
  <Input
    type="date"
    value={formatDate(startDate, 'yyyy-MM-dd')}
    onChange={(e, data) => data.value && setStartDate(new Date(data.value))}
  />
</div>

<div className={styles.filterGroup}>
  <Text className={styles.filterLabel}>End Date</Text>
  <Input
    type="date"
    value={formatDate(endDate, 'yyyy-MM-dd')}
    onChange={(e, data) => data.value && setEndDate(new Date(data.value))}
  />
</div>

          <div className={styles.filterGroup}>
            <Text className={styles.filterLabel}>Action</Text>
            <Dropdown
              placeholder="All actions"
              value={actionFilter ? getActionLabel(actionFilter) : 'All actions'}
              onOptionSelect={(e, data) => setActionFilter(data.optionValue || '')}
              style={{ minWidth: '150px' }}
            >
       <Option value="" text="All actions">All actions</Option>
{actions.map((action) => (
  <Option key={action.value} value={action.value} text={action.label}>
    {action.label}
  </Option>
))}
            </Dropdown>
          </div>

          <div className={styles.filterGroup}>
            <Text className={styles.filterLabel}>Entity Type</Text>
            <Dropdown
              placeholder="All types"
              value={entityTypeFilter ? getEntityTypeLabel(entityTypeFilter) : 'All types'}
              onOptionSelect={(e, data) => setEntityTypeFilter(data.optionValue || '')}
              style={{ minWidth: '150px' }}
            >
    <Option value="" text="All types">All types</Option>
{entityTypes.map((type) => (
  <Option key={type.value} value={type.value} text={type.label}>
    {type.label}
  </Option>
))}
            </Dropdown>
          </div>

          <div className={styles.filterGroup}>
            <Text className={styles.filterLabel}>Search</Text>
            <Input
              contentBefore={<Search24Regular />}
              placeholder="Search..."
              value={searchTerm}
              onChange={(e, data) => setSearchTerm(data.value)}
              style={{ minWidth: '200px' }}
            />
          </div>

          <div className={styles.actions}>
            <Button
              icon={<ArrowClockwise24Regular />}
              onClick={() => {
                setPage(1);
                searchAudit();
              }}
            >
              Search
            </Button>
            <Button
              icon={isExporting ? <Spinner size="tiny" /> : <ArrowDownload24Regular />}
              appearance="subtle"
              onClick={() => handleExport('csv')}
              disabled={isExporting}
            >
              Export CSV
            </Button>
            <Button
              icon={isExporting ? <Spinner size="tiny" /> : <ArrowDownload24Regular />}
              appearance="subtle"
              onClick={() => handleExport('json')}
              disabled={isExporting}
            >
              Export JSON
            </Button>
          </div>
        </div>
      </Card>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* Results Table */}
      <Card className={styles.tableCard}>
        {isLoading ? (
          <Loading message="Searching audit log..." />
        ) : entries.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <DocumentSearch24Regular style={{ fontSize: '48px', color: tokens.colorNeutralForeground3 }} />
            <Text style={{ display: 'block', marginTop: '16px' }}>No audit entries found</Text>
          </div>
        ) : (
          <>
            <Table className={styles.table}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Timestamp</TableHeaderCell>
                  <TableHeaderCell>User</TableHeaderCell>
                  <TableHeaderCell>Action</TableHeaderCell>
                  <TableHeaderCell>Entity Type</TableHeaderCell>
                  <TableHeaderCell>Details</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <Text size={200}>
                      {formatDate(new Date(entry.timestamp), 'PPp')}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text>{entry.userDisplayName}</Text>
                    </TableCell>
                    <TableCell>
                      <span className={styles.actionBadge}>
                        {getActionLabel(entry.action)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Text>{getEntityTypeLabel(entry.entityType)}</Text>
                    </TableCell>
                    <TableCell>
                      <div className={styles.detailsJson}>
                        {JSON.stringify(entry.details)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className={styles.pagination}>
              <Text className={styles.paginationInfo}>
                Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} entries
              </Text>
              <div className={styles.paginationButtons}>
                <Button
                  appearance="subtle"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Text style={{ padding: '0 12px' }}>
                  Page {page} of {totalPages}
                </Text>
                <Button
                  appearance="subtle"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};
